import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ArticleCrm } from 'src/app/classes/article-crm.class';
import { ArticleDayreport } from 'src/app/classes/article-dayreport.class';
import { Article } from 'src/app/classes/article.class';
import { Customer } from 'src/app/classes/customer.class';
import { Project } from 'src/app/classes/project.class';
import { ApiService } from './api/api.service';

export interface AfasSyncResult {
    synced: number;
    failed: { id: number; crm_article_id: string; error: string }[];
}

// One row per customer, or per project when that customer has project-specific bestekposten.
export interface UnprocessedOverviewGroup {
    customer: Customer;
    project: Project | null;
    dayreport_count: number;
    first_date: Date;
    last_date: Date;
}

@Injectable({ providedIn: 'root' })
export class ArticlesService {
    constructor(private apiService: ApiService) {}

    public getList(): Observable<Article[]> {
        return this.apiService.getCallData$<Article[]>('articles');
    }

    public getByCustomer(customerId: number): Observable<ArticleCrm[]> {
        return this.apiService.getCallData$<ArticleCrm[]>(`articles/customer/${customerId}`);
    }

    public getProjectsByCustomer(customerId: number): Observable<Project[]> {
        return this.apiService.getCallData$<Project[]>(`articles/customer/${customerId}/projects`);
    }

    public updateCrmArticle(
        articleCrmId: number,
        changes: Partial<Pick<ArticleCrm, 'article_id' | 'material_id' | 'afas_project_id'>>
    ): Observable<ArticleCrm> {
        return this.apiService
            .patchCall$<ArticleCrm>(`articles/crm/${articleCrmId}`, changes)
            .pipe(map(response => response.data));
    }

    public importForCustomer(
        customerId: number,
        rows: Partial<ArticleCrm>[],
        afasProjectId: string | null = null
    ): Observable<ArticleCrm[]> {
        return this.apiService
            .postCall$<ArticleCrm[]>(`articles/customer/${customerId}/import`, {
                rows,
                afas_project_id: afasProjectId,
            })
            .pipe(map(response => response.data));
    }

    // Used by the "link bestekpost" dialog: unlike updateCrmArticle (a direct in-place edit),
    // this duplicates the bestekpost server-side when it's already linked to a different
    // article/material combo, so one bestekpost can serve multiple combinations.
    public linkCrmArticle(
        articleCrmId: number,
        changes: Pick<ArticleCrm, 'article_id' | 'material_id'>
    ): Observable<ArticleCrm> {
        return this.apiService
            .postCall$<ArticleCrm>(`articles/crm/${articleCrmId}/link`, changes)
            .pipe(map(response => response.data));
    }

    public deleteArticleCrm(articleCrmId: number): Observable<number> {
        return this.apiService.deleteCall$<number>(`articles/crm/${articleCrmId}`).pipe(map(response => response.data));
    }

    public deleteUnlinkedForCustomer(customerId: number, afasProjectId: string | null): Observable<number[]> {
        return this.apiService
            .deleteCall$<number[]>(
                `articles/customer/${customerId}/unlinked`,
                afasProjectId ? { afas_project_id: afasProjectId } : {}
            )
            .pipe(map(response => response.data));
    }

    public deleteAllForCustomer(customerId: number, afasProjectId: string | null): Observable<number[]> {
        return this.apiService
            .deleteCall$<number[]>(
                `articles/customer/${customerId}`,
                afasProjectId ? { afas_project_id: afasProjectId } : {}
            )
            .pipe(map(response => response.data));
    }

    public syncToAfas(articleCrmId: number): Observable<void> {
        return this.apiService.postCall$<void>(`articles/crm/${articleCrmId}/sync-afas`).pipe(map(() => undefined));
    }

    public syncCustomerToAfas(customerId: number): Observable<AfasSyncResult> {
        return this.apiService
            .postCall$<AfasSyncResult>(`articles/customer/${customerId}/sync-afas`)
            .pipe(map(response => response.data));
    }

    public getProductionByCustomer(customerId: number): Observable<ArticleDayreport[]> {
        return this.apiService.getCallData$<ArticleDayreport[]>(`articles/dayreports/customer/${customerId}`);
    }

    public getUnprocessedOverview(): Observable<UnprocessedOverviewGroup[]> {
        return this.apiService.getCallData$<UnprocessedOverviewGroup[]>('articles/dayreports/unprocessed-overview');
    }
}
